import React from 'react';
import dayjs from 'dayjs';
import { ArrowForward } from '@mui/icons-material';
import type { MetricHRSummary } from '@demind-inc/core';
import {
  formatSleepScore,
  normalizeSleepStages,
  SleepSummaryRichPayload,
  toPositiveNumber,
  toTotalSleepHours,
} from './contentCardUtils';
import { SleepBar } from '../../KanbanView/CoreActions/CoreActionModal/ActionModalViews/DailyAnalysisModalView/SleepAnalysis/SleepBar';
import { DescriptionBox } from '../DescriptionBox';
import './richContext.scss';
import { useTranslation } from 'react-i18next';
import { formatDataDate } from '../../DataView/i18n';
import type { DataTFunction } from '../../DataView/i18n';

type Props = {
  p: SleepSummaryRichPayload;
};

const formatClock = (value?: string): string => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed.slice(0, 5);
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
};

const formatDuration = (durationMin: number | undefined, t: DataTFunction): string => {
  if (typeof durationMin !== 'number' || !Number.isFinite(durationMin) || durationMin <= 0)
    return '';
  const hours = Math.floor(durationMin / 60);
  const minutes = durationMin % 60;
  if (hours > 0 && minutes > 0) return t('task.duration.hoursMinutesShort', { hours, minutes });
  if (hours > 0) return t('task.duration.hoursShort', { hours });
  return t('task.duration.minutesShort', { minutes });
};

const formatSleepDate = (value: unknown, language: string): string => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '';
  return formatDataDate(language, parsed.toDate(), { month: 'short', day: 'numeric' });
};

const normalizeSleepClockInput = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const clock = trimmed.length === 5 ? `${trimmed}:00` : trimmed;
    return dayjs().format('YYYY-MM-DD') + `T${clock}`;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.toISOString() : undefined;
};

const SleepSummaryContent: React.FC<Props> = ({ p }) => {
  const { i18n, t } = useTranslation();
  const sleepScore = formatSleepScore(
    (typeof p.efficiency === 'number' ? p.efficiency : p.quality_score) ?? undefined
  );
  const stages = normalizeSleepStages(p.stages);
  const hasValidStage = !!stages && (stages.deep + stages.light + stages.rem + stages.awake) > 0;
  const totalSleepHours = toTotalSleepHours(p.duration_h);
  const hasDurationData = totalSleepHours.hours > 0 || totalSleepHours.minutes > 0;
  const normalizedSleepTime = normalizeSleepClockInput(p.sleep_time);
  const normalizedAwakeTime = normalizeSleepClockInput(p.awake_time);
  const sleepTimeClock = formatClock(p.sleep_time);
  const awakeTimeClock = formatClock(p.awake_time);
  const firstHistory = Array.isArray(p.history) ? p.history[0] : undefined;
  const heartRateSummaryEntries: MetricHRSummary = {
    restingHRBpm: toPositiveNumber(p.heart_rate_summary?.resting_hr_bpm ?? p.resting_hr),
    avgHRBpm: toPositiveNumber(p.heart_rate_summary?.avg_hr_bpm ?? p.avg_hr_bpm),
    minHRBpm: toPositiveNumber(p.heart_rate_summary?.min_hr_bpm ?? p.min_hr_bpm),
    maxHRBpm: toPositiveNumber(p.heart_rate_summary?.max_hr_bpm ?? p.max_hr_bpm),
    avgHRVRmssd: toPositiveNumber(p.heart_rate_summary?.avg_hrv_rmssd ?? p.avg_hrv_rmssd),
    avgHRVSdnn: toPositiveNumber(p.heart_rate_summary?.avg_hrv_sdnn ?? p.avg_hrv_sdnn),
  };
  const hasHeartRateSummary = Object.values(heartRateSummaryEntries).some(
    (value) => typeof value === 'number'
  );
  const sleepDateText =
    formatSleepDate(p.sleep_time, i18n.language) ||
    formatSleepDate(firstHistory?.sleep_time, i18n.language) ||
    formatSleepDate(p.sleep_date, i18n.language);

  const getSuggestedSleepValue = (key: string): unknown => {
    const suggestedSleep = p.suggested_sleep;
    if (!suggestedSleep || typeof suggestedSleep !== 'object') return undefined;
    return (suggestedSleep as Record<string, unknown>)[key];
  };

  const getSuggestedSleepString = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const value = getSuggestedSleepValue(key);
      if (typeof value === 'string' && value.trim()) return value;
    }
    return undefined;
  };

  const getSuggestedSleepNumber = (keys: string[]): number | undefined => {
    for (const key of keys) {
      const value = getSuggestedSleepValue(key);
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    }
    return undefined;
  };

  const suggestedSleepStart = formatClock(
    getSuggestedSleepString(['start_time', 'startTime', 'suggested_start_time', 'ideal_sleep_time'])
  );
  const suggestedSleepEnd = formatClock(
    getSuggestedSleepString(['end_time', 'endTime', 'suggested_end_time', 'ideal_wake_time'])
  );
  const suggestedSleepDuration = formatDuration(
    getSuggestedSleepNumber(['duration_min', 'durationMin', 'duration_minutes']),
    t
  );
  const suggestedSleepReason = getSuggestedSleepString(['reason']);
  const suggestedSleepRange =
    suggestedSleepStart && suggestedSleepEnd
      ? `${suggestedSleepStart}-${suggestedSleepEnd}`
      : suggestedSleepStart || suggestedSleepEnd;
  const suggestedSleepFallback = formatClock(
    typeof p.ideal_sleep_time_tonight === 'string' ? p.ideal_sleep_time_tonight : undefined
  );
  const suggestedSleepPrimaryText = suggestedSleepRange || suggestedSleepFallback;
  const hasSleepRangeSplit = !!suggestedSleepStart && !!suggestedSleepEnd;

  const hasRenderableSleepSummary =
    hasDurationData ||
    !!sleepTimeClock ||
    !!awakeTimeClock ||
    hasHeartRateSummary ||
    !!sleepScore ||
    !!suggestedSleepPrimaryText ||
    !!suggestedSleepReason;

  if (!hasRenderableSleepSummary) return null;

  const shouldShowSleepSummary = hasDurationData || !!normalizedSleepTime || !!normalizedAwakeTime;

  return (
    <div className="rc-content-body">
      {shouldShowSleepSummary && (
        <div className="rc-sleep-summary-wrap">
          {!!sleepDateText && <span className="rc-sleep-date">{sleepDateText}</span>}
          {!!hasDurationData && (
            <span className="rc-sleep-duration">
              {t('task.duration.hoursMinutesShort', {
                hours: totalSleepHours.hours,
                minutes: totalSleepHours.minutes,
              })}
            </span>
          )}
          <SleepBar
            sleepStage={hasValidStage ? stages : undefined}
            sleepTime={normalizedSleepTime}
            awakeTime={normalizedAwakeTime}
          />
          {hasHeartRateSummary && (
            <div className="rc-stat-row">
              {typeof heartRateSummaryEntries.restingHRBpm === 'number' && (
                <div className="rc-stat-item">
                  <span className="rc-stat-value">
                    {Math.round(heartRateSummaryEntries.restingHRBpm)} bpm
                  </span>
                  <span className="rc-stat-label">{t('task.richContext.hr.restingHrLabel')}</span>
                </div>
              )}
              {typeof heartRateSummaryEntries.avgHRBpm === 'number' && (
                <div className="rc-stat-item">
                  <span className="rc-stat-value">
                    {Math.round(heartRateSummaryEntries.avgHRBpm)} bpm
                  </span>
                  <span className="rc-stat-label">{t('task.richContext.hr.avgHrLabel')}</span>
                </div>
              )}
              {typeof heartRateSummaryEntries.minHRBpm === 'number' && (
                <div className="rc-stat-item">
                  <span className="rc-stat-value">
                    {Math.round(heartRateSummaryEntries.minHRBpm)} bpm
                  </span>
                  <span className="rc-stat-label">{t('task.richContext.hr.minHrLabel')}</span>
                </div>
              )}
              {typeof heartRateSummaryEntries.maxHRBpm === 'number' && (
                <div className="rc-stat-item">
                  <span className="rc-stat-value">
                    {Math.round(heartRateSummaryEntries.maxHRBpm)} bpm
                  </span>
                  <span className="rc-stat-label">{t('task.richContext.hr.maxHrLabel')}</span>
                </div>
              )}
              {typeof heartRateSummaryEntries.avgHRVRmssd === 'number' && (
                <div className="rc-stat-item">
                  <span className="rc-stat-value">
                    {Math.round(heartRateSummaryEntries.avgHRVRmssd)} ms
                  </span>
                  <span className="rc-stat-label">{t('task.richContext.hr.avgHrvRmssdLabel')}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!!sleepScore && (
        <div className="rc-pill-row">
          <div className="rc-tag-pill">
            <span className="rc-tag-pill-text">
              {t('task.richContext.sleep.efficiency', { score: sleepScore })}
            </span>
          </div>
        </div>
      )}

      {!!suggestedSleepPrimaryText && (
        <div
          className={`rc-suggested-sleep-card${
            shouldShowSleepSummary ? ' rc-suggested-sleep-card--after-summary' : ''
          }`}
        >
          <div className="rc-suggested-sleep-header">
            <span className="rc-suggested-sleep-label">
              {t('task.richContext.sleep.suggestedSleepWindow')}
            </span>
            {!!suggestedSleepDuration && (
              <span className="rc-suggested-sleep-duration-chip">{suggestedSleepDuration}</span>
            )}
          </div>
          {hasSleepRangeSplit ? (
            <div className="rc-suggested-sleep-time-row">
              <span className="rc-suggested-sleep-time-value">{suggestedSleepStart}</span>
              <ArrowForward className="rc-suggested-sleep-arrow" />
              <span className="rc-suggested-sleep-time-value">{suggestedSleepEnd}</span>
            </div>
          ) : (
            <span className="rc-suggested-sleep-range">{suggestedSleepPrimaryText}</span>
          )}
        </div>
      )}

      {!!suggestedSleepReason && <DescriptionBox description={suggestedSleepReason} />}
    </div>
  );
};

export default SleepSummaryContent;
